import { DateOnly } from '@if/core/date'
import {
  EApplicantType,
  EContractStatus,
  EDecision,
  EDocumentType,
  EOriginSystemId,
  ERequiredFrom,
} from '@if/core/types'
import { Banner, DashedButton, Icon, useCan } from '@if/mui'
import { access } from '@src/access'
import { Box, Card, CardContent, Chip, Divider, Stack } from '@mui/material'
import { UserPlus } from 'lucide-react'
import { FinancingConfigDto } from '@src/data/api/configs-api/financing-config-api'
import { getCanEdit } from '@src/data/creditapp-selectors'
import { useMerchant } from '@src/containers/App/MerchantProvider'
import { useMerchantById } from '@src/data/api/merchants-api/merchants-api'
import { FinancingProgramConfigs } from '@src/types/Constants'
import { CreditApplication } from '@src/types/CreditApplicationSchema'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { canPollCreditApp, isServerAnalysingCredit, isServerCreatingContract } from '../credit-hooks'
import ApplicantInfoBox from './applicantInfoBox'
import ApplicationProgress from './ApplicationProgress'
import DocumentTable from './DocumentSection/DocumentTable'
import {
  canSendOrDeleteHighEstimateInvoice,
  isIdentityVerificationDocument,
} from './DocumentSection/documentTableRow-hooks'
import LoanInfoBox from './LoanInfoBox'
import { MerchantReferenceCodeField } from './MerchantReferenceCodeField'
import ViewApplicationSkeleton from './ViewApplicationSkeleton'
import NotesCard from './NotesCard'
import { useUnreadMessagesPopup } from '../unreadMessagesPopup-hooks'

interface Props {
  creditApplication: CreditApplication
  financingConfig: FinancingConfigDto
}

// Page body: receives the loaded credit application from the PageContainer and renders it.
const ViewCreditApplicationBody = ({ creditApplication, financingConfig }: Props) => {
  const { t } = useTranslation()
  // The session merchant is loaded once at boot in MerchantProvider (it never changes for a session),
  // but a banner views its children's applications: every config / payment-plan lookup below has to
  // key on the merchant that owns the application. Same resolution WorksheetWrapper already does.
  const sessionMerchant = useMerchant()
  const isOwnApplication = sessionMerchant.id === creditApplication.merchantId
  const [applicationMerchant, isFetchingApplicationMerchant] = useMerchantById(
    isOwnApplication ? '' : creditApplication.merchantId,
  )
  const merchant = applicationMerchant ?? sessionMerchant
  const { id, financingProgramId } = creditApplication

  const [error, setError] = useState<Error | null>(null)

  // Prompts the merchant to open the chat when this application has unread messages.
  useUnreadMessagesPopup(creditApplication)

  // The server is analysing / creating the contract → editing is locked and the page polls (in usePrimary).
  const shouldPoll =
    (isServerAnalysingCredit(creditApplication) || isServerCreatingContract(creditApplication)) &&
    canPollCreditApp(creditApplication)

  const canEdit = getCanEdit(creditApplication.editLocked)
  const currentProgramConfig = FinancingProgramConfigs[financingProgramId]

  useEffect(() => {
    const worksheet = creditApplication.worksheet
    if (!worksheet?.isHighEstimate || !worksheet?.firstPaymentOn) {
      return
    }

    const hasClinicInvoice = creditApplication.requiredDocuments.some(
      (doc) => doc.typeId === EDocumentType.ClinicInvoice,
    )

    if (hasClinicInvoice && !canSendOrDeleteHighEstimateInvoice(new DateOnly(worksheet.firstPaymentOn))) {
      setError(new Error(t('common.errors.highEstimateUploadInvoiceDelayPassed')))
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [creditApplication, t])

  const isPrequalComplete =
    creditApplication.prequalificationDecision?.decision === EDecision.Approved ||
    creditApplication.prequalificationDecision?.decision === EDecision.Refused

  const isIdentityVerificationEnabled = useCan(access.identityVerification)

  const identityVerificationByApplicant = useMemo(
    () => ({
      [EApplicantType.Applicant]: creditApplication.applicant?.identityVerification ?? null,
      [EApplicantType.CoApplicant]: creditApplication.coApplicant?.identityVerification ?? null,
    }),
    [creditApplication.applicant?.identityVerification, creditApplication.coApplicant?.identityVerification],
  )

  const requiredDocuments = creditApplication.requiredDocuments.filter(
    (doc) =>
      (doc.requiredFrom !== ERequiredFrom.CreditAgent ||
        isIdentityVerificationDocument(
          doc,
          identityVerificationByApplicant[doc.applicantType],
          isIdentityVerificationEnabled,
        )) &&
      !(
        doc.typeId === EDocumentType.SixMonthsPersonalBankStatements &&
        currentProgramConfig.shouldDisplayBankStatementDocument === false
      ),
  )

  const canAddCoApplicant =
    creditApplication.contract?.cvtNumber === null &&
    canEdit &&
    !shouldPoll &&
    creditApplication.originSystemId === EOriginSystemId.MerchantDashboard &&
    currentProgramConfig.supportsCoApplicant

  const showCoApplicantColumn = Boolean(creditApplication.coApplicant) || canAddCoApplicant

  if (isFetchingApplicationMerchant) return <ViewApplicationSkeleton />

  return (
    <>
      {error && (
        <Box sx={{ mb: 2 }}>
          <Banner severity="error" title={t('common.error')}>
            {error.message}
          </Banner>
        </Box>
      )}
      <Card>
        <CardContent>
          <Stack spacing={2}>
            <LoanInfoBox creditApp={creditApplication} />
            <Divider />
            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3}>
              {creditApplication.applicant && (
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <ApplicantInfoBox
                    financingProgramId={financingProgramId}
                    applicant={creditApplication.applicant}
                    applicantTitle={t('viewCreditApplication.applicant')}
                    applicantType={EApplicantType.Applicant}
                    creditApplication={creditApplication}
                    disabled={
                      creditApplication.contract?.status === EContractStatus.Completed ||
                      !canEdit ||
                      isPrequalComplete ||
                      shouldPoll
                    }
                  />
                </Box>
              )}
              {creditApplication.applicant && showCoApplicantColumn && (
                <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', lg: 'block' } }} />
              )}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {creditApplication.coApplicant ? (
                  <ApplicantInfoBox
                    financingProgramId={financingProgramId}
                    applicant={creditApplication.coApplicant}
                    applicantTitle={t('viewCreditApplication.coApplicant')}
                    applicantType={EApplicantType.CoApplicant}
                    creditApplication={creditApplication}
                    disabled={
                      creditApplication.contract?.status === EContractStatus.Completed ||
                      !canEdit ||
                      shouldPoll ||
                      isPrequalComplete
                    }
                  />
                ) : (
                  canAddCoApplicant && (
                    <DashedButton
                      fullWidth
                      startIcon={<Icon icon={UserPlus} size="sm" />}
                      href={`/credits/${creditApplication.financingProgramId}/${id}/edit?addCoApp=true`}
                      sx={{ height: '100%' }}
                    >
                      {t('viewCreditApplication.addCoapp')}
                    </DashedButton>
                  )
                )}
              </Box>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
      <Stack spacing={2} sx={{ mt: 2 }}>
        {merchant.supportedFinancingPrograms.length > 1 && (
          <Chip
            label={`${t('applicationHeader.program')} ${t(`EFinancingProgram.${creditApplication.financingProgramId}`, { ns: 'enum' })}`}
            sx={{ alignSelf: 'flex-start' }}
          />
        )}
        <MerchantReferenceCodeField creditApp={creditApplication} />
      </Stack>
      <ApplicationProgress
        creditApp={creditApplication}
        merchant={merchant}
        financingConfig={financingConfig}
        canEdit={canEdit}
        isPolling={shouldPoll}
      />
      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems="flex-start" sx={{ mt: 2 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <DocumentTable
            financingProgramId={financingProgramId}
            requiredDocuments={requiredDocuments}
            creditApplicationId={id}
            lastSentOn={creditApplication.merchantDocumentUploadRequestSentOn}
            setError={setError}
            canEdit={canEdit}
            worksheet={creditApplication.worksheet}
            identityVerificationByApplicant={identityVerificationByApplicant}
            isMerchantDashboardOrigin={creditApplication.originSystemId === EOriginSystemId.MerchantDashboard}
          />
        </Box>
        <Box sx={{ width: { xs: '100%', lg: 420 }, flexShrink: 0 }}>
          <NotesCard creditApp={creditApplication} />
        </Box>
      </Stack>
    </>
  )
}

export default ViewCreditApplicationBody
